import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { getSearchResultState, getSearchResultParams } from '../../core/pipes/search/search.functions';
import * as _ from 'lodash';
import { setParams } from '../../core/config/core.httpclient-utils';

@Injectable()
export class RelatedSearchService {
    constructor(private http: HttpClient) { }

    getDestinationState(item: any): Promise<any> {
        if (item.section === 'forummessage') {
            return this.http.get(`/api/forummessage/${item.refId}/location`)
                .toPromise()
                .then((data: any) => {
                    return {
                        state: 'home.project.summary.forum.read',
                        params: {
                            unixName: item.project || '',
                            id: data.forum || 0,
                            thread: data.forumThread || 0,
                            page_num: Math.ceil(_.get(data, 'number', 1) / 20)
                        },
                        options: {
                            notify: true,
                            reload: false,
                            inherit: true
                        }
                    };
                })
                .catch((error: any) => { throw error; });
        } else {
            return new Promise<any>((resolve, reject) => {
                const state = getSearchResultState(item);
                const params = getSearchResultParams(item);
                resolve({
                    state: state,
                    params: params,
                    options: {}
                });
            });
        }
    }

    createExternalRequest(url: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            const request = new XMLHttpRequest();
            request.open('GET', url, true);
            request.onreadystatechange = () => {
                if (request.readyState === 4) {
                    if (request.status === 200) {
                        const response = JSON.parse(request.response);
                        resolve(response);
                    } else {
                        const response = JSON.parse(request.response);
                        resolve([{ title: response.error_message, link: '' }]);
                    }
                }
            };
            request.onerror = () => {
                reject();
            };
            request.send();
        });
    }

    cancelSearch: any = false;
    private cancelSearchSubject: Subject<void> = new Subject<void>();

    doSearch(search: string, sections: string, maxPageSize: number): Promise<void> {
        if (this.cancelSearch && this.cancelSearchSubject.isStopped) {
            this.cancelSearchSubject.next();
            this.cancelSearchSubject.complete();
        }

        this.cancelSearchSubject = new Subject<void>();

        const params: any = {
            q: search,
            s: sections ? sections : 'trackeritem,wiki,docman,scmcommit,forum',
            page_num: 1,
            page_size: maxPageSize || 5
        };


        const gforgeRequest = this.http.get('/api/search/', { params: setParams(params), observe: 'response', responseType: 'json' })
            .pipe(
                map((response: any) => response.body),
                catchError((error: any) => { throw error; })
            );

        const soRequest = this.createExternalRequest('https://api.stackexchange.com/2.2/search/advanced?pagesize=5&site=stackoverflow&q=' + search)
            .catch((error: any) => { throw error; });

        return combineLatest([gforgeRequest, soRequest])
            .toPromise()
            .then((ret: any) => { return ret; }, (error: any) => { throw error; });
    }

    blankResult(): any {
        return {
            sameSection: [],
            sameProject: [],
            otherProject: [],
            otherSources: [],
            total: 0
        };
    }

    sortResults(results: any, section: string, sectionTitle: string, project: any): any {
        const gforgeSorted = _.orderBy(
            _.get(results, '0.items', []),
            ['matchScore', 'title'],
            ['desc', 'asc']
        );

        const sameSection = (item: any) => item.section === section && item.description === project.name + '/' + sectionTitle;
        const sameProject = (item: any) => !sameSection(item) && _.get(item, 'project', -1) === project.unixName;
        const otherProject = (item: any) => _.get(item, 'project', -1) !== project.unixName;

        const ret = {
            sameSection: gforgeSorted.filter(sameSection).slice(0, 5),
            sameProject: gforgeSorted.filter(sameProject).slice(0, 5),
            otherProject: gforgeSorted.filter(otherProject).slice(0, 5),
            otherSources: _.get(results, '1.items', []).slice(0, 5),
            total: 0
        };

        ret.total = ret.sameSection.length + ret.sameProject.length + ret.otherProject.length + ret.otherSources.length;

        return ret;
    }
}
